import { PluginGeoLocation, PluginWorkSite } from '../models';
import { isDefined } from '@realwork/crux-utils/array';
import { calcCrowDistanceInMiles } from '@realwork/crux-geo/calcCrowDistanceInMiles';

const MAX_DISTANCE_IN_MILES = 300;

export const boundsFromWorkSites = (
    workSites: PluginWorkSite[],
    accountLocation?: google.maps.LatLngLiteral
) => {
    const bounds = new google.maps.LatLngBounds();

    workSites
        .filter((workSite) => {
            // filter out the worksites that are too far out of bounds for the center
            if (
                accountLocation &&
                workSite.geolocation?.lng &&
                workSite.geolocation.lng
            ) {
                return (
                    calcCrowDistanceInMiles(
                        accountLocation.lat,
                        accountLocation.lng,
                        workSite.geolocation.lat,
                        workSite.geolocation.lng
                    ) < MAX_DISTANCE_IN_MILES
                );
            }
            return true;
        })
        .map((workSite) => workSite.geolocation)
        .filter((geolocation) => geolocation?.lng && geolocation?.lat)
        .forEach((geolocation) => {
            bounds.extend(geolocation);
        });

    return bounds;
};

function rad2degr(rad: number) {
    return (rad * 180) / Math.PI;
}
function degr2rad(degr: number) {
    return (degr * Math.PI) / 180;
}

function getLatLngCenter(
    latLngInDegr: { lat: number; lng: number }[]
): PluginGeoLocation {
    let sumX = 0;
    let sumY = 0;
    let sumZ = 0;
    let lat;
    let lng;
    for (let i = 0; i < latLngInDegr.length; i++) {
        const point = latLngInDegr[i];
        lat = degr2rad(point.lat);
        lng = degr2rad(point.lng);
        // sum of cartesian coordinates
        sumX += Math.cos(lat) * Math.cos(lng);
        sumY += Math.cos(lat) * Math.sin(lng);
        sumZ += Math.sin(lat);
    }

    const avgX = sumX / latLngInDegr.length;
    const avgY = sumY / latLngInDegr.length;
    const avgZ = sumZ / latLngInDegr.length;

    // convert average x, y, z coordinate to latitude and longitude
    lng = Math.atan2(avgY, avgX);
    const hyp = Math.sqrt(avgX * avgX + avgY * avgY);
    lat = Math.atan2(avgZ, hyp);

    return {
        latitude: rad2degr(lat),
        longitude: rad2degr(lng),
    };
}

export function getCenterPoint(workSites: PluginWorkSite[]): PluginGeoLocation {
    const locations = workSites
        .map((workSite) => workSite.geolocation)
        .filter(isDefined)
        .filter((location) => location.lat && location.lng);

    return getLatLngCenter(locations);
}
